// ==================== 统一弹框（确认 / 提示）====================
// 替代原生 window.confirm / window.alert（在 WebView 内会带 URL 标题、样式不可控）。
// 命令式调用，返回 Promise：showConfirm 用户点确定 resolve(true)、取消 resolve(false)；
// showAlert 仅一个按钮，点击后 resolve(true)。
// 全局单实例：由挂在 App 根部的 <AppDialog/> 渲染 dialogState。

import { reactive } from 'vue'

export interface DialogOptions {
  /** 标题，可选；不传则不显示标题行 */
  title?: string
  /** 正文内容 */
  message: string
  /** 确定按钮文案，默认「确定」 */
  confirmText?: string
  /** 取消按钮文案，默认「取消」 */
  cancelText?: string
}

interface DialogState {
  visible: boolean
  title: string
  message: string
  confirmText: string
  cancelText: string
  /** 是否显示取消按钮：confirm 为 true，alert 为 false */
  showCancel: boolean
  resolve: ((result: boolean) => void) | null
}

export const dialogState = reactive<DialogState>({
  visible: false,
  title: '',
  message: '',
  confirmText: '确定',
  cancelText: '取消',
  showCancel: true,
  resolve: null,
})

function open(opts: DialogOptions, showCancel: boolean): Promise<boolean> {
  // 已有弹框未关闭：先把上一个按取消 resolve，避免 Promise 泄漏
  if (dialogState.resolve) {
    dialogState.resolve(false)
    dialogState.resolve = null
  }
  return new Promise<boolean>((resolve) => {
    dialogState.title = opts.title ?? ''
    dialogState.message = opts.message
    dialogState.confirmText = opts.confirmText ?? '确定'
    dialogState.cancelText = opts.cancelText ?? '取消'
    dialogState.showCancel = showCancel
    dialogState.resolve = resolve
    dialogState.visible = true
  })
}

function settle(result: boolean): void {
  if (!dialogState.visible) return
  dialogState.visible = false
  const resolve = dialogState.resolve
  dialogState.resolve = null
  resolve?.(result)
}

/** 确认弹框（确定 / 取消两个按钮），返回是否点了确定 */
export function showConfirm(opts: DialogOptions): Promise<boolean> {
  return open(opts, true)
}

/** 提示弹框（仅一个确定按钮），点击后 resolve(true) */
export function showAlert(opts: DialogOptions | string): Promise<boolean> {
  const normalized = typeof opts === 'string' ? { message: opts } : opts
  return open(normalized, false)
}

/** 供 <AppDialog/> 内部调用，勿在业务里直接使用 */
export function _confirmDialog(): void {
  settle(true)
}
export function _cancelDialog(): void {
  settle(false)
}
